import { useLayoutEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  AdminObservability,
  type ObsModelRow,
  type ObsRpm,
  type ObsTimePoint,
  type ObsToolRow,
  type ObsTopRow,
} from "../../lib/apiTyped";
import { useResource } from "../../lib/useResource";
import { useApiCall } from "../../lib/useToastError";
import { PageHeader } from "../../components/AppLayout";
import { Card, Badge } from "../../components/shared/Card";
import { Button } from "../../components/shared/Button";
import { FormInput } from "../../components/shared/FormInput";
import { DataTable, type Column } from "../../components/shared/DataTable";
import { fmtNumber, fmtUsd, fmtPct } from "../../lib/format";

// ---- date helpers (UTC, matching the backend's day boundaries) ----

function todayUtc(): string {
  return new Date().toISOString().slice(0, 10);
}
function dayUtcOffset(offsetDays: number): string {
  return new Date(Date.now() + offsetDays * 86400000).toISOString().slice(0, 10);
}

// ---- chart helpers (local — the trend chart is the only caller) ----

// Compact numeric label for chart axes: 1.2k, 3.4M, 890.
function fmtCompact(n: number): string {
  const abs = Math.abs(n);
  if (abs >= 1_000_000) return `${(n / 1_000_000).toFixed(abs >= 10_000_000 ? 0 : 1)}M`;
  if (abs >= 1_000) return `${(n / 1_000).toFixed(abs >= 10_000 ? 0 : 1)}k`;
  if (abs >= 1) return `${Math.round(n)}`;
  return n === 0 ? "0" : n.toFixed(2);
}

// Round a max up to a "nice" number (1/2/5 × 10^k) so the Y ticks read cleanly.
function niceMax(max: number): number {
  if (max <= 0) return 1;
  const pow = Math.pow(10, Math.floor(Math.log10(max)));
  const norm = max / pow;
  const nice = norm <= 1 ? 1 : norm <= 2 ? 2 : norm <= 5 ? 5 : 10;
  return nice * pow;
}

// "2026-07-05" → "5 jul" (forced UTC so the day never shifts by timezone).
function shortDate(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString("es-ES", {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

// Average requests-per-minute. Coarse (whole-range mean, not a peak) — the true
// intraday peak needs sub-daily aggregation, landing in a later phase.
function fmtRpm(rpm: number): string {
  if (!Number.isFinite(rpm)) return "—";
  if (rpm < 10) return rpm.toFixed(2);
  if (rpm < 100) return rpm.toFixed(1);
  return fmtNumber(Math.round(rpm));
}

// Local wall-clock "HH:MM" for an ISO instant (intraday X axis + peak label).
function fmtClock(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
}

// ---- small presentational pieces (local — single use, not shared/) ----

function KpiCard({
  label,
  value,
  sub,
}: {
  label: string;
  value: string;
  sub?: string;
}) {
  return (
    <Card className="p-5">
      <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
        {label}
      </div>
      <div className="mt-2 text-2xl font-black text-ink">{value}</div>
      {sub && <div className="text-xs text-ink-3 mt-0.5">{sub}</div>}
    </Card>
  );
}

// KPI whose data source (tools / errors / latency) isn't instrumented yet.
// Same footprint as KpiCard but visibly muted so it doesn't read as a bug.
function NoDataCard({ label }: { label: string }) {
  return (
    <Card className="p-5" style={{ opacity: 0.6 }}>
      <div className="flex items-center justify-between gap-2">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
          {label}
        </div>
        <Badge label="Pronto" color="#9a7d1e" />
      </div>
      <div className="mt-2 text-2xl font-black text-ink-3">—</div>
      <div className="text-xs text-ink-3 mt-0.5">Pendiente de instrumentación</div>
    </Card>
  );
}

function Bar({ pct }: { pct: number }) {
  return (
    <div
      className="mt-1 h-1 rounded-full overflow-hidden"
      style={{ backgroundColor: "rgba(0,0,0,0.06)" }}
    >
      <div
        className="h-full transition-all"
        style={{ width: `${Math.min(100, pct)}%`, backgroundColor: "#0f0f0f" }}
      />
    </div>
  );
}

type SparkMetric = "costUsd" | "requests" | "tokens";

function sparkValue(p: ObsTimePoint, m: SparkMetric): number {
  if (m === "costUsd") return p.costUsd;
  if (m === "requests") return p.requests;
  return p.tokens.total;
}
function sparkLabel(p: ObsTimePoint, m: SparkMetric): string {
  if (m === "costUsd") return fmtUsd(p.costUsd);
  if (m === "requests") return `${fmtNumber(p.requests)} req`;
  return `${fmtNumber(p.tokens.total)} tok`;
}

// Y-axis tick label, formatted per metric ($ for cost, compact otherwise).
function axisLabel(v: number, m: SparkMetric): string {
  if (m === "costUsd") return v >= 1 ? `$${fmtCompact(v)}` : `$${v.toFixed(2)}`;
  return fmtCompact(v);
}

// Axis text must read at the SAME px size as the surrounding DOM. The SVG scales
// its viewBox to the container width, which would magnify any font size baked in
// viewBox units (an 11-unit label renders ~16px on a laptop, larger still on a
// wide monitor). Measure the rendered width and counter-scale so labels land at
// ~targetPx on every screen. Called before any early return (Rules of Hooks).
function useAxisFont(viewBoxWidth: number, targetPx = 11) {
  const ref = useRef<SVGSVGElement>(null);
  const [fontSize, setFontSize] = useState(targetPx);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const w = el.getBoundingClientRect().width;
      if (w > 0) setFontSize((targetPx * viewBoxWidth) / w);
    };
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [viewBoxWidth, targetPx]);
  return { ref, fontSize };
}

// Dependency-free trend chart with X/Y axes. The repo draws its charts as bare
// SVG; a single line series doesn't justify pulling in a charting lib (CLAUDE.md
// = minimize). A real-coordinate viewBox (NOT preserveAspectRatio="none") keeps
// the axis text un-sheared; width:100% + height:auto scales it to the container.
function TrendChart({ points, metric }: { points: ObsTimePoint[]; metric: SparkMetric }) {
  const { ref, fontSize } = useAxisFont(720);
  if (points.length === 0) {
    return (
      <div className="text-sm text-ink-3 py-10 text-center">
        Sin datos en el rango.
      </div>
    );
  }
  const values = points.map((p) => sparkValue(p, metric));
  const rawMax = Math.max(...values, 0);
  const max = niceMax(rawMax);
  const peakIdx = values.reduce((best, v, i) => (v > values[best] ? i : best), 0);

  const W = 720;
  const H = 240;
  const ML = 56; // room for Y labels
  const MR = 14;
  const MT = 14;
  const MB = 30; // room for X dates
  const plotW = W - ML - MR;
  const plotH = H - MT - MB;
  const n = values.length;
  const x = (i: number) => (n <= 1 ? ML + plotW / 2 : ML + (i * plotW) / (n - 1));
  const y = (v: number) => MT + plotH - (v / max) * plotH;
  const baseY = MT + plotH;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  // ~6 evenly spaced date labels, always including the first and last point.
  const xTickCount = Math.min(6, n);
  const xTickIdx =
    n <= 1
      ? [0]
      : Array.from({ length: xTickCount }, (_, k) =>
          Math.round((k * (n - 1)) / (xTickCount - 1)),
        );

  const line = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${x(0).toFixed(1)},${baseY} ${line} ${x(n - 1).toFixed(1)},${baseY}`;

  const peakY = y(values[peakIdx]);
  const peakLabelY = peakY < MT + 16 ? peakY + 16 : peakY - 8;

  return (
    <svg ref={ref} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      {/* Y gridlines + labels */}
      {yTicks.map((v, i) => (
        <g key={i}>
          <line
            x1={ML}
            y1={y(v)}
            x2={W - MR}
            y2={y(v)}
            stroke="rgba(0,0,0,0.06)"
            strokeWidth={1}
          />
          <text
            x={ML - 8}
            y={y(v)}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={fontSize}
            fill="#9ca3af"
          >
            {axisLabel(v, metric)}
          </text>
        </g>
      ))}
      {/* X date labels */}
      {xTickIdx.map((i) => (
        <text
          key={i}
          x={x(i)}
          y={H - 10}
          textAnchor="middle"
          fontSize={fontSize}
          fill="#9ca3af"
        >
          {shortDate(points[i].date)}
        </text>
      ))}
      {/* Area + line */}
      <polyline points={area} fill="rgba(15,15,15,0.06)" stroke="none" />
      <polyline points={line} fill="none" stroke="#0f0f0f" strokeWidth={1.5} />
      {/* Peak marker + value */}
      {rawMax > 0 && (
        <>
          <circle cx={x(peakIdx)} cy={peakY} r={3.5} fill="#0f0f0f" />
          <text
            x={x(peakIdx)}
            y={peakLabelY}
            textAnchor="middle"
            fontSize={fontSize}
            fontWeight={700}
            fill="#0f0f0f"
          >
            {sparkLabel(points[peakIdx], metric)}
          </text>
        </>
      )}
      {/* Invisible hover targets (native tooltip per point) */}
      {points.map((p, i) => (
        <circle key={p.date} cx={x(i)} cy={y(values[i])} r={8} fill="transparent">
          <title>{`${shortDate(p.date)} · ${sparkLabel(p, metric)}`}</title>
        </circle>
      ))}
    </svg>
  );
}

// Preset ranges (in days) offered as one-tap chips. "Custom" opens a small
// inline panel with two date inputs + an explicit "Aplicar" so a refetch only
// fires once the range is confirmed, not on every keystroke.
const RANGE_PRESETS = [7, 30, 90];

function DateRangePicker({
  from,
  to,
  onChange,
}: {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
}) {
  const activePreset = RANGE_PRESETS.find(
    (d) => to === todayUtc() && from === dayUtcOffset(-(d - 1)),
  );
  const [customOpen, setCustomOpen] = useState(activePreset === undefined);
  const [draftFrom, setDraftFrom] = useState(from);
  const [draftTo, setDraftTo] = useState(to);

  function applyPreset(days: number) {
    setCustomOpen(false);
    onChange(dayUtcOffset(-(days - 1)), todayUtc());
  }
  function openCustom() {
    setDraftFrom(from);
    setDraftTo(to);
    setCustomOpen(true);
  }
  const validRange = draftFrom !== "" && draftTo !== "" && draftFrom <= draftTo;
  const customActive = customOpen && activePreset === undefined;

  return (
    <div className="flex flex-col items-start gap-2">
      {/* Segmented presets + custom toggle */}
      <div
        className="inline-flex items-center gap-1 rounded-full p-1"
        style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
      >
        {RANGE_PRESETS.map((d) => {
          const active = activePreset === d;
          return (
            <button
              key={d}
              type="button"
              onClick={() => applyPreset(d)}
              className="px-3.5 py-1 text-xs font-semibold rounded-full transition-all"
              style={{
                backgroundColor: active ? "#111111" : "transparent",
                color: active ? "#ffffff" : "#0f0f0f",
              }}
            >
              {d}d
            </button>
          );
        })}
        <button
          type="button"
          onClick={openCustom}
          className="px-3.5 py-1 text-xs font-semibold rounded-full transition-all"
          style={{
            backgroundColor: customActive ? "#111111" : "transparent",
            color: customActive ? "#ffffff" : "#0f0f0f",
          }}
        >
          Personalizado
        </button>
      </div>

      {/* Custom range panel */}
      {customOpen && (
        <div
          className="flex items-end gap-2 rounded-2xl p-3"
          style={{
            backgroundColor: "rgba(0,0,0,0.03)",
            border: "1px solid rgba(0,0,0,0.08)",
          }}
        >
          <div className="w-36">
            <FormInput label="Desde" type="date" value={draftFrom} onChange={setDraftFrom} />
          </div>
          <div className="w-36">
            <FormInput label="Hasta" type="date" value={draftTo} onChange={setDraftTo} />
          </div>
          <Button
            size="sm"
            variant="primary"
            onClick={() => onChange(draftFrom, draftTo)}
            disabled={!validRange}
          >
            Aplicar
          </Button>
        </div>
      )}
      {customOpen && !validRange && (
        <div className="text-[11px]" style={{ color: "#C61418" }}>
          «Desde» debe ser anterior o igual a «Hasta».
        </div>
      )}
    </div>
  );
}

// Per-minute line chart for the intraday RPM window. Same bare-SVG approach as
// TrendChart, but the X axis is wall-clock time and there are far more points
// (up to 2880), so it skips per-point hover targets and just marks the peak.
function MinuteChart({ data }: { data: ObsRpm | null }) {
  const { ref, fontSize } = useAxisFont(720);
  if (!data || data.values.length === 0) {
    return (
      <div className="text-sm text-ink-3 py-10 text-center">
        Sin datos en la ventana.
      </div>
    );
  }
  const values = data.values;
  const startMs = new Date(data.start).getTime();
  const stepMs = data.stepSec * 1000;
  const rawMax = Math.max(...values, 0);
  const max = niceMax(rawMax);
  const peakIdx = values.reduce((best, v, i) => (v > values[best] ? i : best), 0);

  const W = 720;
  const H = 240;
  const ML = 48;
  const MR = 14;
  const MT = 14;
  const MB = 30;
  const plotW = W - ML - MR;
  const plotH = H - MT - MB;
  const n = values.length;
  const x = (i: number) => (n <= 1 ? ML + plotW / 2 : ML + (i * plotW) / (n - 1));
  const y = (v: number) => MT + plotH - (v / max) * plotH;
  const baseY = MT + plotH;

  const yTicks = [0, 0.25, 0.5, 0.75, 1].map((f) => f * max);
  const xTickCount = Math.min(6, n);
  const xTickIdx =
    n <= 1
      ? [0]
      : Array.from({ length: xTickCount }, (_, k) =>
          Math.round((k * (n - 1)) / (xTickCount - 1)),
        );
  const clockAt = (i: number) => fmtClock(new Date(startMs + i * stepMs).toISOString());

  const line = values.map((v, i) => `${x(i).toFixed(1)},${y(v).toFixed(1)}`).join(" ");
  const area = `${x(0).toFixed(1)},${baseY} ${line} ${x(n - 1).toFixed(1)},${baseY}`;
  const peakY = y(values[peakIdx]);
  const peakLabelY = peakY < MT + 16 ? peakY + 16 : peakY - 8;

  return (
    <svg ref={ref} viewBox={`0 0 ${W} ${H}`} style={{ width: "100%", height: "auto" }}>
      {yTicks.map((v, i) => (
        <g key={i}>
          <line
            x1={ML}
            y1={y(v)}
            x2={W - MR}
            y2={y(v)}
            stroke="rgba(0,0,0,0.06)"
            strokeWidth={1}
          />
          <text
            x={ML - 8}
            y={y(v)}
            textAnchor="end"
            dominantBaseline="middle"
            fontSize={fontSize}
            fill="#9ca3af"
          >
            {fmtCompact(v)}
          </text>
        </g>
      ))}
      {xTickIdx.map((i) => (
        <text
          key={i}
          x={x(i)}
          y={H - 10}
          textAnchor="middle"
          fontSize={fontSize}
          fill="#9ca3af"
        >
          {clockAt(i)}
        </text>
      ))}
      <polyline points={area} fill="rgba(15,15,15,0.06)" stroke="none" />
      <polyline points={line} fill="none" stroke="#0f0f0f" strokeWidth={1.25} />
      {rawMax > 0 && (
        <>
          <circle cx={x(peakIdx)} cy={peakY} r={3.5} fill="#0f0f0f" />
          <text
            x={x(peakIdx)}
            y={peakLabelY}
            textAnchor="middle"
            fontSize={fontSize}
            fontWeight={700}
            fill="#0f0f0f"
          >
            {`${fmtNumber(values[peakIdx])} rpm · ${clockAt(peakIdx)}`}
          </text>
        </>
      )}
    </svg>
  );
}

// Intraday requests-per-minute section. Independent 24h/48h window (separate
// from the daily range above) since the RPM peak is only meaningful fine-grained
// and short-horizon. Surfaces the true per-minute peak + a suggested quota so
// the provider's RPM limit can be dimensioned against the burst, not the mean.
function IntradayRpm() {
  const [hours, setHours] = useState<24 | 48>(24);
  const rpm = useResource(() => AdminObservability.rpm({ hours }), [hours]);
  const d = rpm.data;
  const suggested =
    d && d.peakRpm > 0 ? Math.ceil(d.peakRpm * 1.3) : null;

  return (
    <Card className="p-5 mt-6">
      <div className="flex items-start justify-between gap-3 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Requests por minuto (intradía)
          </div>
          <div className="text-[11px] text-ink-3 mt-0.5">
            Pico real por minuto — para dimensionar la cuota de RPM en Google Cloud.
          </div>
        </div>
        <div
          className="inline-flex items-center gap-1 rounded-full p-1"
          style={{ backgroundColor: "rgba(0,0,0,0.05)" }}
        >
          {([24, 48] as const).map((h) => (
            <button
              key={h}
              type="button"
              onClick={() => setHours(h)}
              className="px-3.5 py-1 text-xs font-semibold rounded-full transition-all"
              style={{
                backgroundColor: hours === h ? "#111111" : "transparent",
                color: hours === h ? "#ffffff" : "#0f0f0f",
              }}
            >
              {h}h
            </button>
          ))}
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-3 mb-4">
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Peak RPM
          </div>
          <div className="mt-1 text-2xl font-black text-ink">
            {d ? fmtNumber(d.peakRpm) : "—"}
          </div>
          <div className="text-xs text-ink-3">
            {d?.peakAt ? `pico a las ${fmtClock(d.peakAt)}` : "sin tráfico en la ventana"}
          </div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            RPM medio (ventana)
          </div>
          <div className="mt-1 text-2xl font-black text-ink">
            {d ? fmtRpm(d.avgRpm) : "—"}
          </div>
          <div className="text-xs text-ink-3">últimas {hours}h</div>
        </div>
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Cuota sugerida
          </div>
          <div className="mt-1 text-2xl font-black text-ink">
            {suggested != null ? fmtNumber(suggested) : "—"}
          </div>
          <div className="text-xs text-ink-3">pico × 1,3 (margen)</div>
        </div>
      </div>

      <MinuteChart data={d} />
    </Card>
  );
}

// ---- page ----

export function AdminObservabilityPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const from = searchParams.get("from") ?? dayUtcOffset(-29);
  const to = searchParams.get("to") ?? todayUtc();
  const [refreshKey, setRefreshKey] = useState(0);
  const [sparkMetric, setSparkMetric] = useState<SparkMetric>("costUsd");
  const { run } = useApiCall();
  const [exporting, setExporting] = useState(false);

  const deps = [from, to, refreshKey];
  const overview = useResource(() => AdminObservability.overview({ from, to }), deps);
  const byModel = useResource(() => AdminObservability.byModel({ from, to }), deps);
  const series = useResource(() => AdminObservability.timeseries({ from, to }), deps);
  const topOrgs = useResource(
    () => AdminObservability.topOrgs({ from, to, limit: 10 }),
    deps,
  );
  const tools = useResource(() => AdminObservability.tools({ from, to }), deps);

  function setRange(nextFrom: string, nextTo: string) {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set("from", nextFrom);
      next.set("to", nextTo);
      return next;
    });
  }

  async function doExport(format: "csv" | "json", dataset: "daily" | "raw") {
    setExporting(true);
    const blob = await run(() =>
      AdminObservability.exportBlob({ from, to, format, dataset }),
    );
    setExporting(false);
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `observability-${dataset}-${from}_${to}.${format}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  const o = overview.data;
  const totals = o?.totals;
  const rangeDays = o?.range.days ?? 0;
  const avgRpm =
    totals && rangeDays > 0 ? totals.requests / (rangeDays * 1440) : null;

  const modelColumns: Column<ObsModelRow>[] = [
    {
      header: "Modelo",
      render: (m) => (
        <div>
          <div className="text-ink">{m.displayName}</div>
          <div className="text-xs text-ink-3">{m.provider || "—"}</div>
        </div>
      ),
    },
    {
      header: "Mix de uso",
      render: (m) => (
        <div className="min-w-[140px]">
          <div className="text-xs text-ink-3">{m.pct.toFixed(1)}%</div>
          <Bar pct={m.pct} />
        </div>
      ),
    },
    { header: "Requests", align: "right", render: (m) => fmtNumber(m.requests) },
    { header: "Tokens", align: "right", render: (m) => fmtNumber(m.tokens.total) },
    {
      header: "Cache hit",
      align: "right",
      render: (m) => fmtPct(m.cacheHitRatio),
    },
    { header: "Coste", align: "right", render: (m) => fmtUsd(m.usdCost) },
  ];

  return (
    <>
      <PageHeader
        title="Observabilidad de agentes"
        subtitle={`Rango ${from} → ${to}${o ? ` · ${o.range.days} días` : ""}`}
        action={
          <div className="flex flex-wrap items-start gap-3">
            <DateRangePicker from={from} to={to} onChange={setRange} />
            <Button size="sm" variant="ghost" onClick={() => setRefreshKey((k) => k + 1)}>
              Actualizar
            </Button>
          </div>
        }
      />

      {/* KPIs */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          label="Coste (USD)"
          value={fmtUsd(totals?.usdCost)}
          sub="coste estimado del rango"
        />
        <KpiCard label="Requests" value={fmtNumber(totals?.requests)} sub="turnos de agente" />
        <KpiCard
          label="RPM medio"
          value={avgRpm != null ? fmtRpm(avgRpm) : "—"}
          sub="req/min · media del rango (pico real abajo)"
        />
        <KpiCard
          label="Tokens"
          value={fmtNumber(totals?.tokens.total)}
          sub={
            totals
              ? `in ${fmtNumber(totals.tokens.input)} · out ${fmtNumber(totals.tokens.output)}`
              : undefined
          }
        />
        <KpiCard
          label="Cache hit ratio"
          value={fmtPct(totals?.cacheHitRatio)}
          sub="lecturas cacheadas / input"
        />
        <KpiCard label="Orgs activas" value={fmtNumber(totals?.activeOrgs)} />
        {o?.errorRate.available ? (
          <KpiCard label="Tasa de error" value={fmtPct(o.errorRate.value)} />
        ) : (
          <NoDataCard label="Tasa de error" />
        )}
        {o?.latency.available ? (
          <KpiCard
            label="Latencia p95"
            value={o.latency.p95Ms != null ? `${fmtNumber(Math.round(o.latency.p95Ms))} ms` : "—"}
            sub={
              o.latency.avgMs != null
                ? `media ${fmtNumber(Math.round(o.latency.avgMs))} ms`
                : undefined
            }
          />
        ) : (
          <NoDataCard label="Latencia p95" />
        )}
        <KpiCard
          label="Usuarios activos"
          value={o?.activeUsers.available ? fmtNumber(o.activeUsers.value) : "—"}
          sub={
            o && !o.activeUsers.available
              ? "solo últimos 90 días"
              : "únicos en el rango"
          }
        />
      </div>

      {/* Trend */}
      <Card className="p-5 mt-6">
        <div className="flex items-center justify-between gap-3 mb-3">
          <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
            Tendencia diaria
          </div>
          <div className="flex gap-1">
            {(
              [
                ["costUsd", "Coste"],
                ["requests", "Requests"],
                ["tokens", "Tokens"],
              ] as Array<[SparkMetric, string]>
            ).map(([key, label]) => (
              <Button
                key={key}
                size="sm"
                variant={sparkMetric === key ? "primary" : "ghost"}
                onClick={() => setSparkMetric(key)}
              >
                {label}
              </Button>
            ))}
          </div>
        </div>
        <TrendChart points={series.data?.points ?? []} metric={sparkMetric} />
      </Card>

      {/* Intraday requests-per-minute (own 24h/48h window) */}
      <IntradayRpm />

      {/* By model */}
      <div className="mt-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-3">
          Consumo por modelo
        </div>
        <DataTable
          rows={byModel.data?.models ?? null}
          loading={byModel.loading}
          columns={modelColumns}
          rowKey={(m) => m.modelId}
          empty={{ title: "Sin actividad de modelos en este rango." }}
        />
      </div>

      {/* Top orgs + tool usage */}
      <div className="grid gap-6 lg:grid-cols-2 mt-6">
        <TopConsumers
          title="Top organizaciones"
          rows={topOrgs.data?.rows ?? null}
          loading={topOrgs.loading}
          error={topOrgs.error}
        />
        <ToolUsage rows={tools.data?.rows ?? null} loading={tools.loading} />
      </div>

      {/* Export */}
      <Card className="p-5 mt-6">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-3 mb-1">
          Exportar para análisis local
        </div>
        <p className="text-xs text-ink-3 mb-3">
          Agregado diario anónimo, o eventos crudos (con userId, últimos 90 días — quedan auditados).
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-ink-2">Agregado:</span>
          <Button size="sm" variant="ghost" disabled={exporting} onClick={() => doExport("csv", "daily")}>
            CSV
          </Button>
          <Button size="sm" variant="ghost" disabled={exporting} onClick={() => doExport("json", "daily")}>
            JSON
          </Button>
          <span className="text-xs text-ink-2 ml-3">Eventos crudos (90d):</span>
          <Button size="sm" variant="ghost" disabled={exporting} onClick={() => doExport("csv", "raw")}>
            CSV
          </Button>
          <Button size="sm" variant="ghost" disabled={exporting} onClick={() => doExport("json", "raw")}>
            JSON
          </Button>
        </div>
      </Card>
    </>
  );
}

function TopConsumers({
  title,
  subtitle,
  rows,
  loading,
  error,
}: {
  title: string;
  subtitle?: string;
  rows: ObsTopRow[] | null;
  loading: boolean;
  error: Error | null;
}) {
  const columns: Column<ObsTopRow>[] = [
    { header: "Cuenta", render: (r) => <span className="text-ink">{r.label}</span> },
    { header: "Requests", align: "right", render: (r) => fmtNumber(r.requests) },
    { header: "Tokens", align: "right", render: (r) => fmtNumber(r.tokens) },
    { header: "Coste", align: "right", render: (r) => fmtUsd(r.costUsd) },
  ];
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
          {title}
        </div>
        {subtitle && <div className="text-[11px] text-ink-3">{subtitle}</div>}
      </div>
      <DataTable
        rows={error ? [] : rows}
        loading={loading}
        columns={columns}
        rowKey={(r) => r.id}
        empty={{
          title: error ? "No disponible en este rango" : "Sin consumidores en este rango.",
          description: error ? error.message : undefined,
        }}
      />
    </div>
  );
}

function ToolUsage({
  rows,
  loading,
}: {
  rows: ObsToolRow[] | null;
  loading: boolean;
}) {
  const columns: Column<ObsToolRow>[] = [
    { header: "Herramienta", render: (r) => <span className="text-ink">{r.name}</span> },
    { header: "Llamadas", align: "right", render: (r) => fmtNumber(r.calls) },
    { header: "Errores", align: "right", render: (r) => fmtNumber(r.errors) },
    { header: "Tasa de error", align: "right", render: (r) => fmtPct(r.errorRate) },
  ];
  return (
    <div>
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-xs font-semibold uppercase tracking-wider text-ink-3">
          Uso de herramientas (MCP)
        </div>
      </div>
      <DataTable
        rows={rows}
        loading={loading}
        columns={columns}
        rowKey={(r) => r.name}
        empty={{
          title: "Sin datos de herramientas en este rango.",
          description:
            "Se rellenará cuando la app de chat emita telemetría v3 (tools, latencia y errores).",
        }}
      />
    </div>
  );
}
